import { sortBy } from "lodash";
import { ImageAssetSchema } from "./ImageAsset";
import * as z from "zod";
import { SpecialPricePeriod } from "./SpecialPricePeriod";

export const DiscountStrategySchema = z.union([
  z.literal("Percentage"),
  z.literal("Fixed"),
]);
export type DiscountStrategy = z.infer<typeof DiscountStrategySchema>;

export const ProductIdSchema = z.string();
export type ProductId = z.infer<typeof ProductIdSchema>;
export const ProductSchema = z.object({
  id: ProductIdSchema,
  title: z.string(),
  subTitle: z.string(),
  price: z.number(),
  discountStrategy: DiscountStrategySchema.default("Percentage"),
  discountFixed: z.number().min(0).default(0),
  discountPercentage: z.number().max(100).min(0).default(0),
  priority: z.number().default(0),
  description: z.string(),
  coverAssetId: z.string().optional().nullable(),

  // Readonly
  coverAsset: z.optional(ImageAssetSchema).nullable(),
  coverUrl: z.string().nullable(),

  categories: z.array(z.string()).default([]),
});
export const ProductsSchema = z.array(ProductSchema);

export type Product = z.infer<typeof ProductSchema>;

export function emptyProduct() {
  return Product.createDefault();
}

export namespace Product {
  export function createDefault(): Product {
    return {
      id: "",
      title: "",
      subTitle: "",
      price: 0,
      priority: 0,
      description: "",
      coverUrl: "",
      discountFixed: 0,
      discountPercentage: 0,
      discountStrategy: "Percentage",
      categories: [],
    };
  }

  export function sameAs(a: Product, b: Product) {
    return a.id === b.id;
  }

  export function sortByPriceAndPriorityProperties() {
    return [(p: Product) => p.price, (p: Product) => p.priority];
  }

  export function sortByPriceAndPriority<
    T extends { id: string; priority: number; price: number }
  >(
    products: Array<T>,
    opts?: {
      specialPricePeriods?: Map<ProductId, Array<SpecialPricePeriod>>;
      forDate?: Date;
    }
  ): Array<T> {
    return sortBy(
      sortBy(products, (p) =>
        getPrice(p, opts?.specialPricePeriods?.get(p.id), opts?.forDate)
      ),
      (p) => p.priority
    );
  }

  /**
   * Adjusts price on product to match the special price period
   */
  export function getPrice(
    product: Pick<Product, "price" | "id">,
    specialPricePeriods: Array<SpecialPricePeriod> = [],
    now?: Date
  ) {
    now ??= new Date();

    for (const spp of specialPricePeriods) {
      if (spp.productId !== product.id) continue;

      if (now >= spp.period.from && now <= spp.period.to) {
        return spp.price;
      }
    }

    return product.price;
  }
}
